import { TaskT } from 'ui-io/task-io/task-t'

import booleanExtractors from '../column-values/boolean-extractors'
import numberExtractors from '../column-values/number-extractors'
import dateExtractors from '../column-values/date-extractors'
import stringExtractors from '../column-values/string-extractors'
import {
  TaskValueKey,
  undefinedExtractors,
} from '../column-values/value-extractors'
import {
  Extractor,
  getBooleanComparator,
  getNumberComparator,
  getDateComparator,
  getStringComparator,
  Comparator,
  getUndefinedComparator,
} from '../../../utils/comparators'

export type TaskSortKey = TaskValueKey
type TaskSorters = Record<TaskSortKey, Sorter<TaskT>>

type Sorter<Item> = (
  invertOrdering?: boolean,
) => (inboxEntryA: Item, inboxEntryB: Item) => number

type MoreSortersConfig<Item, T> = {
  extractors: Partial<Record<TaskSortKey, Extractor<Item, T>>>
  comparator: Comparator<Item, T>
}

// For some entries we might want to set the standard sort direction to the opposite of the default
const FLIP_SORT_ORDER_FOR: Partial<Record<TaskSortKey, boolean>> = {}
const getUndefined = () => undefined
const makeSorters = <Item, T>({
  extractors,
  comparator,
}: MoreSortersConfig<Item, T>) => {
  const sorters: Partial<Record<TaskSortKey, Sorter<Item>>> = {}
  for (const key of Object.keys(extractors) as (keyof typeof extractors)[]) {
    sorters[key] = comparator(
      extractors[key] ?? getUndefined, // the default will never be used in practice, just there for TS
      FLIP_SORT_ORDER_FOR?.[key] ?? false,
    )
  }
  return sorters
}

const taskSorters: TaskSorters = {
  ...makeSorters({
    extractors: booleanExtractors,
    comparator: getBooleanComparator,
  }),
  ...makeSorters({
    extractors: numberExtractors,
    comparator: getNumberComparator,
  }),
  ...makeSorters({
    extractors: dateExtractors,
    comparator: getDateComparator,
  }),
  ...makeSorters({
    extractors: stringExtractors,
    comparator: getStringComparator,
  }),
  ...makeSorters({
    extractors: undefinedExtractors,
    comparator: getUndefinedComparator,
  }),
} as TaskSorters // use as to remove "partial status"

export default taskSorters
